'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useWalletAuth } from '@/contexts/WalletAuthContext';
import { createClient } from '@/lib/supabase/client';
import { Upload, Globe, Send, MessageSquare, FileText, CheckCircle, AlertTriangle, Loader2, Info, ArrowRight, Star, Link2, Search, Newspaper, Phone, Mail, Plus, Trash2, DollarSign, Shield, ChevronDown, ChevronUp, Image, X, TrendingUp } from 'lucide-react';

const XIcon = ({ size = 15, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const YoutubeIcon = ({ size = 15, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const GithubIcon = ({ size = 15, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
  </svg>
);

interface AirdropPhase {
  phase_number: number;
  phase_name: string;
  description: string;
  start_date: string;
  end_date: string;
  reward_amount: string;
  status: string;
}

interface SocialPlatform {
  id: string;
  name: string;
  slug: string;
  placeholder: string;
  is_active: boolean;
}

type FormData = {
  projectName: string;
  tokenName: string;
  tokenSymbol: string;
  blockchain: string;
  website: string;
  whitepaper: string;
  roadmap: string;
  telegramGroup: string;
  telegramChannel: string;
  twitter: string;
  discord: string;
  instagram: string;
  youtube: string;
  medium: string;
  github: string;
  reddit: string;
  linkedin: string;
  tiktok: string;
  facebook: string;
  category: string;
  projectType: string;
  networkStatus: string;
  exchangeLink: string;
  dexLink: string;
  explorerLink: string;
  contractAddress: string;
  tokenAllocation: string;
  totalSupply: string;
  tokenPrice: string;
  shortDescription: string;
  rewardPool: string;
  newsSection: string;
  airdropRules: string;
  eligibilityRequirements: string;
  startDate: string;
  endDate: string;
  distributionDate: string;
  vestingDetails: string;
  communicationTelegram: string;
  communicationEmail: string;
  communicationWhatsapp: string;
  kycVerification: boolean;
  contactEmail: string;
  additionalNotes: string;
};

const blockchains = ['Ethereum', 'Solana', 'BNB Chain', 'Arbitrum', 'Base', 'Polygon', 'Avalanche', 'Optimism', 'Aptos', 'Sui', 'Other'];
const categories = ['AI Coins', 'AI Agents', 'DeFi', 'DEX', 'RWA', 'DePIN', 'Layer 1', 'Layer 2', 'Stablecoins', 'Meme Coins', 'GameFi', 'Privacy Coins', 'Interoperability', 'Launchpad', 'NFT', 'Other'];

const steps = [
  { id: 'step-1', label: 'Project Identity', num: 1 },
  { id: 'step-2', label: 'Social & Links', num: 2 },
  { id: 'step-3', label: 'Token Details', num: 3 },
  { id: 'step-4', label: 'Campaign Rules', num: 4 },
  { id: 'step-5', label: 'Contact & Verify', num: 5 },
];

const inputCls = "w-full bg-[#07111F] border border-[rgba(22,194,255,0.15)] rounded-lg px-4 py-3 text-sm text-[#E6F1FF] placeholder-[#93A4B8]/50 focus:outline-none focus:border-[rgba(22,194,255,0.5)] transition-colors font-dm";
const iconInputCls = "w-full bg-[#07111F] border border-[rgba(22,194,255,0.15)] rounded-lg pl-10 pr-4 py-3 text-sm text-[#E6F1FF] placeholder-[#93A4B8]/50 focus:outline-none focus:border-[rgba(22,194,255,0.5)] transition-colors font-dm";

// ─── Drag-Drop Image Upload Component ────────────────────────────────────────
interface ImageUploadProps {
  label: string;
  hint: string;
  accept?: string;
  onFileChange: (file: File | null) => void;
  file: File | null;
  previewUrl: string | null;
  aspectHint?: string;
}

function DragDropImageUpload({ label, hint, accept = 'image/*', onFileChange, file, previewUrl, aspectHint }: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped && dropped.type.startsWith('image/')) {
      onFileChange(dropped);
    } else {
      toast.error('Please drop a valid image file.');
    }
  }, [onFileChange]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-2">{label}</label>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !previewUrl && inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl transition-all duration-200 overflow-hidden ${
          isDragging
            ? 'border-[#16C2FF] bg-[rgba(22,194,255,0.08)]'
            : previewUrl
            ? 'border-[rgba(0,229,168,0.3)] bg-[rgba(0,229,168,0.04)]'
            : 'border-[rgba(22,194,255,0.2)] bg-[#07111F] hover:border-[rgba(22,194,255,0.4)] cursor-pointer'
        }`}
        style={{ minHeight: 120 }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => onFileChange(e.target.files?.[0] || null)}
        />

        {previewUrl ? (
          <div className="relative flex items-center justify-center p-3">
            <img
              src={previewUrl}
              alt={label}
              className="max-h-40 max-w-full rounded-lg object-contain"
            />
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                className="p-1.5 rounded-lg bg-[rgba(22,194,255,0.15)] text-[#16C2FF] hover:bg-[rgba(22,194,255,0.25)] transition-colors"
                title="Replace image"
              >
                <Image size={12} />
              </button>
              <button
                type="button"
                onClick={handleRemove}
                className="p-1.5 rounded-lg bg-[rgba(255,77,106,0.15)] text-[#FF4D6A] hover:bg-[rgba(255,77,106,0.25)] transition-colors"
                title="Remove image"
              >
                <X size={12} />
              </button>
            </div>
            <div className="absolute bottom-2 left-2 right-2 text-center">
              <span className="text-[10px] text-[#00E5A8] bg-[rgba(0,229,168,0.1)] px-2 py-0.5 rounded-full">
                {file?.name || 'Image selected'}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            {isDragging ? (
              <>
                <div className="w-10 h-10 rounded-full bg-[rgba(22,194,255,0.15)] flex items-center justify-center mb-2">
                  <Upload size={18} className="text-[#16C2FF]" />
                </div>
                <p className="text-sm text-[#16C2FF] font-dm">Drop image here</p>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-[rgba(22,194,255,0.08)] flex items-center justify-center mb-2">
                  <Image size={18} className="text-[#93A4B8]" />
                </div>
                <p className="text-sm text-[#93A4B8] font-dm mb-1">
                  <span className="text-[#16C2FF]">Click to upload</span> or drag & drop
                </p>
                <p className="text-xs text-[#93A4B8]/60 font-dm">{hint}</p>
                {aspectHint && <p className="text-[10px] text-[#93A4B8]/40 mt-1">{aspectHint}</p>}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Live Token Price Badge ───────────────────────────────────────────────────
function LiveTokenPrice({ symbol }: { symbol: string }) {
  const [price, setPrice] = useState<number | null>(null);
  const [change24h, setChange24h] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!symbol || symbol.length < 2) return;
    let cancelled = false;
    const fetch_ = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/coingecko-price?symbols=${encodeURIComponent(symbol.toLowerCase())}`);
        if (!res.ok) return;
        const data = await res.json();
        const entry = data.prices?.[symbol.toLowerCase()];
        if (entry && !cancelled) {
          setPrice(entry.price);
          setChange24h(entry.change24h);
        }
      } catch { /* ignore */ } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const timer = setTimeout(fetch_, 800); // debounce
    return () => { cancelled = true; clearTimeout(timer); };
  }, [symbol]);

  if (!symbol || symbol.length < 2) return null;

  return (
    <div className="flex items-center gap-2 mt-1.5">
      {loading ? (
        <span className="flex items-center gap-1 text-[10px] text-[#93A4B8]">
          <Loader2 size={10} className="animate-spin" /> Fetching live price...
        </span>
      ) : price !== null ? (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-xs font-mono text-[#16C2FF] bg-[rgba(22,194,255,0.08)] px-2 py-0.5 rounded-full border border-[rgba(22,194,255,0.2)]">
            <TrendingUp size={10} />
            ${price < 0.01 ? price.toFixed(6) : price < 1 ? price.toFixed(4) : price.toFixed(2)}
          </span>
          {change24h !== null && (
            <span className={`text-[10px] font-dm ${change24h >= 0 ? 'text-[#00E5A8]' : 'text-[#FF4D6A]'}`}>
              {change24h >= 0 ? '▲' : '▼'} {Math.abs(change24h).toFixed(2)}% (24h)
            </span>
          )}
          <span className="text-[9px] text-[#93A4B8]/50">via CoinGecko</span>
        </div>
      ) : (
        <span className="text-[10px] text-[#93A4B8]/50">No live price found for {symbol.toUpperCase()}</span>
      )}
    </div>
  );
}

export default function ProjectSubmissionForm() {
  const { user } = useAuth();
  const { walletUser } = useWalletAuth();
  const userId = user?.id || walletUser?.id;

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [auditFile, setAuditFile] = useState<File | null>(null);
  const [auditUrl, setAuditUrl] = useState('');
  const [phases, setPhases] = useState<AirdropPhase[]>([]);
  const [showPhases, setShowPhases] = useState(false);
  const [socialPlatforms, setSocialPlatforms] = useState<SocialPlatform[]>([]);
  const [suggestPlatform, setSuggestPlatform] = useState('');
  const [suggestSearch, setSuggestSearch] = useState('');
  const [showSuggestBox, setShowSuggestBox] = useState(false);
  const [dynamicSocials, setDynamicSocials] = useState<Record<string, string>>({});

  const { register, handleSubmit, formState: { errors }, trigger, watch } = useForm<FormData>({ mode: 'onChange' });
  const kycValue = watch('kycVerification');
  const tokenSymbolValue = watch('tokenSymbol');

  // Generate preview URLs when files change
  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setLogoPreview(null);
    }
  }, [logoFile]);

  useEffect(() => {
    if (bannerFile) {
      const url = URL.createObjectURL(bannerFile);
      setBannerPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setBannerPreview(null);
    }
  }, [bannerFile]);

  useEffect(() => {
    const fetchPlatforms = async () => {
      const supabase = createClient();
      const { data } = await supabase.from('social_platforms').select('*').eq('is_active', true).order('name');
      setSocialPlatforms(data || []);
    };
    fetchPlatforms();
  }, []);

  const stepFields: Record<number, (keyof FormData)[]> = {
    1: ['projectName', 'tokenName', 'tokenSymbol', 'blockchain', 'category', 'contactEmail', 'projectType', 'networkStatus'],
    2: ['website', 'twitter', 'telegramGroup'],
    3: ['contractAddress', 'shortDescription', 'rewardPool', 'tokenAllocation'],
    4: ['airdropRules', 'eligibilityRequirements', 'startDate', 'endDate'],
    5: ['kycVerification'],
  };

  const handleNext = async () => {
    const fields = stepFields[currentStep];
    const valid = await trigger(fields);
    if (valid) setCurrentStep((s) => Math.min(s + 1, 5));
  };

  const addPhase = () => {
    setPhases(prev => [...prev, {
      phase_number: prev.length + 1,
      phase_name: `Phase ${prev.length + 1}`,
      description: '',
      start_date: '',
      end_date: '',
      reward_amount: '',
      status: 'upcoming',
    }]);
  };

  const removePhase = (idx: number) => {
    setPhases(prev => prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, phase_number: i + 1 })));
  };

  const updatePhase = (idx: number, field: keyof AirdropPhase, value: string) => {
    setPhases(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleSuggestPlatform = async () => {
    if (!suggestPlatform.trim()) return;
    const supabase = createClient();
    const slug = suggestPlatform.toLowerCase().replace(/\s+/g, '_');
    await supabase.from('social_platforms').insert({
      name: suggestPlatform,
      slug,
      placeholder: `https://${slug}.com/yourproject`,
      is_active: false,
      is_suggested: true,
      suggested_by: userId || null,
    });
    toast.success(`"${suggestPlatform}" suggested! Admin will review and activate it.`);
    setSuggestPlatform('');
    setShowSuggestBox(false);
  };

  const filteredPlatforms = socialPlatforms.filter(p =>
    !suggestSearch || p.name.toLowerCase().includes(suggestSearch.toLowerCase())
  );

  // Upload image to Supabase Storage and return CDN URL
  const uploadImage = async (file: File, folder: string): Promise<string | null> => {
    const supabase = createClient();
    const ext = file.name.split('.').pop() || 'png';
    const path = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('airdrop-images').upload(path, file, {
      cacheControl: '31536000',
      upsert: false,
    });
    if (error) {
      console.error('Image upload error:', error);
      return null;
    }
    const { data: urlData } = supabase.storage.from('airdrop-images').getPublicUrl(path);
    return urlData.publicUrl;
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      const supabase = createClient();
      let finalAuditUrl = auditUrl;
      let finalAuditName = '';
      let finalLogoUrl: string | null = null;
      let finalBannerUrl: string | null = null;

      // Upload logo
      if (logoFile) {
        finalLogoUrl = await uploadImage(logoFile, 'logos');
        if (!finalLogoUrl) toast.error('Logo upload failed, continuing without it.');
      }

      // Upload banner
      if (bannerFile) {
        finalBannerUrl = await uploadImage(bannerFile, 'banners');
        if (!finalBannerUrl) toast.error('Banner upload failed, continuing without it.');
      }

      // Upload audit report
      if (auditFile) {
        const ext = auditFile.name.split('.').pop();
        const path = `audit-reports/${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase.storage.from('project-assets').upload(path, auditFile);
        if (!uploadErr) {
          const { data: urlData } = supabase.storage.from('project-assets').getPublicUrl(path);
          finalAuditUrl = urlData.publicUrl;
          finalAuditName = auditFile.name;
        }
      }

      const { error } = await supabase.from('project_submissions').insert({
        user_id: userId || null,
        project_name: data.projectName,
        token_name: data.tokenName,
        token_symbol: data.tokenSymbol.toUpperCase(),
        blockchain: data.blockchain,
        category: data.category,
        project_type: data.projectType || 'decentralized',
        network_status: data.networkStatus || 'active',
        website: data.website || '',
        whitepaper: data.whitepaper || '',
        roadmap: data.roadmap || '',
        telegram: data.telegramGroup || '',
        telegram_channel: data.telegramChannel || '',
        twitter: data.twitter || '',
        discord: data.discord || '',
        instagram: data.instagram || '',
        youtube: data.youtube || '',
        medium: data.medium || '',
        github: data.github || '',
        reddit: data.reddit || '',
        linkedin: data.linkedin || '',
        tiktok: data.tiktok || '',
        facebook: data.facebook || '',
        exchange_link: data.exchangeLink || '',
        dex_link: data.dexLink || '',
        explorer_link: data.explorerLink || '',
        contract_address: data.contractAddress || '',
        token_allocation: data.tokenAllocation || '',
        total_supply: data.totalSupply || '',
        token_price: data.tokenPrice || '',
        short_description: data.shortDescription || '',
        reward_pool: data.rewardPool || '',
        news_section: data.newsSection || '',
        airdrop_rules: data.airdropRules || '',
        eligibility_requirements: data.eligibilityRequirements || '',
        start_date: data.startDate || null,
        end_date: data.endDate || null,
        distribution_date: data.distributionDate || null,
        vesting_details: data.vestingDetails || '',
        communication_telegram: data.communicationTelegram || '',
        communication_email: data.communicationEmail || '',
        communication_whatsapp: data.communicationWhatsapp || '',
        kyc_verification: data.kycVerification || false,
        contact_email: data.contactEmail || '',
        additional_notes: data.additionalNotes || '',
        audit_report_url: finalAuditUrl || null,
        audit_report_name: finalAuditName || null,
        airdrop_phases: phases.length > 0 ? phases : null,
        logo_url: finalLogoUrl || null,
        banner_url: finalBannerUrl || null,
        status: 'pending',
        is_validated: false,
        validation_notes: '',
        submitted_at: new Date().toISOString(),
      });
      if (error) throw error;
      setSubmitted(true);
      toast.success('Project submitted successfully! Our team will review within 3–5 business days.');
    } catch (err: any) {
      toast.error(err?.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-20 pb-16 min-h-screen flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 rounded-full bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.3)] flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={36} className="text-[#00E5A8]" />
          </div>
          <h2 className="font-sora font-700 text-3xl text-[#E6F1FF] mb-3">Submission Received</h2>
          <p className="text-[#93A4B8] text-lg font-dm mb-2 leading-relaxed">
            Your project has been submitted for review. Our team will verify your submission within 3–5 business days.
          </p>
          <p className="text-xs text-[#93A4B8]/60 font-dm mb-8">
            You will receive a confirmation at the email address you provided. Verified projects are listed in the AIDARAM Airdrop Hub.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="/airdrop-hub" className="btn-primary flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-sora">
              View Airdrop Hub <ArrowRight size={14} />
            </a>
            <a href="/homepage" className="btn-outline px-6 py-3 rounded-xl text-sm">Back to Home</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 pb-16">
      {/* Hero */}
      <div className="relative py-14 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(139,92,246,0.08)_0%,transparent_60%)]" />
        <div className="absolute inset-0 grid-glow opacity-30" />
        <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[rgba(139,92,246,0.1)] border border-[rgba(139,92,246,0.25)] mb-5">
            <Star size={12} className="text-[#8B5CF6]" />
            <span className="text-xs font-medium text-[#8B5CF6] font-sora tracking-wide uppercase">Project Listing</span>
          </div>
          <h1 className="font-sora font-700 text-4xl sm:text-5xl text-[#E6F1FF] mb-4">Submit Your Project</h1>
          <p className="text-[#93A4B8] text-lg font-dm max-w-2xl mx-auto">
            List your airdrop campaign on AIDARAM and reach thousands of verified crypto users.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Step Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between relative">
            <div className="absolute top-4 left-0 right-0 h-px bg-[rgba(22,194,255,0.1)]" />
            <div className="absolute top-4 left-0 h-px bg-[rgba(22,194,255,0.5)] transition-all duration-500" style={{ width: `${((currentStep - 1) / 4) * 100}%` }} />
            {steps.map((step) => (
              <div key={step.id} className="relative flex flex-col items-center gap-2 z-10">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-sora font-700 transition-all duration-300 ${
                  step.num < currentStep ? 'bg-[#16C2FF] text-[#07111F]' :
                  step.num === currentStep ? 'bg-[rgba(22,194,255,0.2)] border-2 border-[#16C2FF] text-[#16C2FF]': 'bg-[#0D1B2A] border border-[rgba(22,194,255,0.2)] text-[#93A4B8]'
                }`}>
                  {step.num < currentStep ? <CheckCircle size={14} /> : step.num}
                </div>
                <span className={`text-xs font-dm hidden sm:block ${step.num === currentStep ? 'text-[#16C2FF]' : 'text-[#93A4B8]'}`}>{step.label}</span>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.12)] rounded-2xl p-6 sm:p-8">

            {/* Step 1: Project Identity */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="font-sora font-700 text-xl text-[#E6F1FF] mb-1">Project Identity</h2>
                  <p className="text-sm text-[#93A4B8] font-dm">Basic information about your project and token.</p>
                </div>

                {/* Logo Upload — Drag & Drop */}
                <DragDropImageUpload
                  label="Project Logo"
                  hint="PNG, SVG, JPG — min 200×200px recommended"
                  aspectHint="Square format (1:1) preferred"
                  file={logoFile}
                  previewUrl={logoPreview}
                  onFileChange={setLogoFile}
                />

                {/* Banner Upload — Drag & Drop */}
                <DragDropImageUpload
                  label="Airdrop Banner"
                  hint="PNG, JPG — 1200×400px or 16:9 recommended"
                  aspectHint="Wide banner format (3:1 or 16:9) preferred"
                  file={bannerFile}
                  previewUrl={bannerPreview}
                  onFileChange={setBannerFile}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Project Name <span className="text-[#FF4D6A]">*</span></label>
                    <input {...register('projectName', { required: 'Project name is required', minLength: { value: 2, message: 'Minimum 2 characters' } })} placeholder="e.g. Nexus Protocol" className={inputCls} />
                    {errors.projectName && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.projectName.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Token Name <span className="text-[#FF4D6A]">*</span></label>
                    <input {...register('tokenName', { required: 'Token name is required' })} placeholder="e.g. Nexus Token" className={inputCls} />
                    {errors.tokenName && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.tokenName.message}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Token Symbol <span className="text-[#FF4D6A]">*</span></label>
                    <input {...register('tokenSymbol', { required: 'Token symbol is required', maxLength: { value: 10, message: 'Max 10 characters' } })} placeholder="e.g. NXS" className={`${inputCls} uppercase font-mono`} />
                    {errors.tokenSymbol && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.tokenSymbol.message}</p>}
                    {/* Live price lookup */}
                    <LiveTokenPrice symbol={tokenSymbolValue || ''} />
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Blockchain <span className="text-[#FF4D6A]">*</span></label>
                    <select {...register('blockchain', { required: 'Please select a blockchain' })} className={inputCls}>
                      <option value="">Select blockchain</option>
                      {blockchains.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                    {errors.blockchain && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.blockchain.message}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Category <span className="text-[#FF4D6A]">*</span></label>
                    <select {...register('category', { required: 'Please select a category' })} className={inputCls}>
                      <option value="">Select category</option>
                      {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.category && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.category.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Contact Email <span className="text-[#FF4D6A]">*</span></label>
                    <input {...register('contactEmail', { required: 'Contact email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' } })} type="email" placeholder="team@yourproject.io" className={inputCls} />
                    {errors.contactEmail && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.contactEmail.message}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Project Type <span className="text-[#FF4D6A]">*</span></label>
                    <select {...register('projectType', { required: 'Please select project type' })} className={inputCls}>
                      <option value="">Select type</option>
                      <option value="decentralized">Decentralized</option>
                      <option value="centralized">Centralized</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                    {errors.projectType && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.projectType.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Network Status</label>
                    <select {...register('networkStatus')} className={inputCls}>
                      <option value="active">Active / Mainnet</option>
                      <option value="testnet">Testnet</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="paused">Paused</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Social & Links */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="font-sora font-700 text-xl text-[#E6F1FF] mb-1">Social & Exchange Links</h2>
                  <p className="text-sm text-[#93A4B8] font-dm">Help us verify your project's online presence. All active platforms are shown below.</p>
                </div>
                <div>
                  <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Website <span className="text-[#FF4D6A]">*</span></label>
                  <div className="relative">
                    <Globe size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                    <input {...register('website', { required: 'Website URL is required', pattern: { value: /^https?:\/\/.+\..+/, message: 'Enter a valid URL starting with https://' } })} placeholder="https://yourproject.io" className={iconInputCls} />
                  </div>
                  {errors.website && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.website.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Whitepaper URL</label>
                  <div className="relative">
                    <FileText size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                    <input {...register('whitepaper')} placeholder="https://docs.yourproject.io/whitepaper" className={iconInputCls} />
                  </div>
                </div>
                {/* Telegram */}
                <div className="border border-[rgba(22,194,255,0.1)] rounded-xl p-4 space-y-4">
                  <p className="text-xs font-sora font-600 text-[#16C2FF] uppercase tracking-wider">Telegram</p>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Telegram Group <span className="text-[#FF4D6A]">*</span></label>
                    <div className="relative">
                      <Send size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                      <input {...register('telegramGroup', { required: 'Telegram group link is required' })} placeholder="https://t.me/yourproject_group" className={iconInputCls} />
                    </div>
                    {errors.telegramGroup && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.telegramGroup.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Telegram Channel</label>
                    <div className="relative">
                      <Send size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                      <input {...register('telegramChannel')} placeholder="https://t.me/yourproject_channel" className={iconInputCls} />
                    </div>
                  </div>
                </div>
                {/* Exchange Links */}
                <div className="border border-[rgba(22,194,255,0.1)] rounded-xl p-4 space-y-4">
                  <p className="text-xs font-sora font-600 text-[#16C2FF] uppercase tracking-wider">Exchange & Explorer Links</p>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Exchange Link (CEX)</label>
                    <div className="relative">
                      <Link2 size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                      <input {...register('exchangeLink')} placeholder="https://binance.com/trade/TOKEN_USDT" className={iconInputCls} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">DEX Link</label>
                    <div className="relative">
                      <Link2 size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                      <input {...register('dexLink')} placeholder="https://app.uniswap.org/swap?outputCurrency=0x..." className={iconInputCls} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Block Explorer Link</label>
                    <div className="relative">
                      <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                      <input {...register('explorerLink')} placeholder="https://etherscan.io/token/0x..." className={iconInputCls} />
                    </div>
                  </div>
                </div>
                {/* Dynamic Social Media Platforms */}
                <div className="border border-[rgba(22,194,255,0.1)] rounded-xl p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-sora font-600 text-[#16C2FF] uppercase tracking-wider">Social Media Platforms</p>
                    <span className="text-[10px] text-[#93A4B8]">{filteredPlatforms.length} platforms available</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Twitter / X <span className="text-[#FF4D6A]">*</span></label>
                      <div className="relative">
                        <XIcon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input {...register('twitter', { required: 'Twitter/X handle is required' })} placeholder="https://x.com/yourproject" className={iconInputCls} />
                      </div>
                      {errors.twitter && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.twitter.message}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Discord</label>
                      <div className="relative">
                        <MessageSquare size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input {...register('discord')} placeholder="https://discord.gg/yourserver" className={iconInputCls} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Instagram</label>
                      <div className="relative">
                        <Globe size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input {...register('instagram')} placeholder="https://instagram.com/yourproject" className={iconInputCls} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">YouTube</label>
                      <div className="relative">
                        <YoutubeIcon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input {...register('youtube')} placeholder="https://youtube.com/@yourproject" className={iconInputCls} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">GitHub</label>
                      <div className="relative">
                        <GithubIcon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input {...register('github')} placeholder="https://github.com/yourproject" className={iconInputCls} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Reddit</label>
                      <div className="relative">
                        <Globe size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input {...register('reddit')} placeholder="https://reddit.com/r/yourproject" className={iconInputCls} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Medium</label>
                      <div className="relative">
                        <FileText size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input {...register('medium')} placeholder="https://medium.com/@yourproject" className={iconInputCls} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">LinkedIn</label>
                      <div className="relative">
                        <Globe size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input {...register('linkedin')} placeholder="https://linkedin.com/company/yourproject" className={iconInputCls} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">TikTok</label>
                      <div className="relative">
                        <Globe size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input {...register('tiktok')} placeholder="https://tiktok.com/@yourproject" className={iconInputCls} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Facebook</label>
                      <div className="relative">
                        <Globe size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input {...register('facebook')} placeholder="https://facebook.com/yourproject" className={iconInputCls} />
                      </div>
                    </div>
                  </div>
                  {/* Suggest new platform */}
                  <div className="border-t border-[rgba(22,194,255,0.08)] pt-4">
                    <p className="text-xs text-[#93A4B8] mb-2">Don't see your platform? Search or suggest it:</p>
                    <div className="flex gap-2 mb-2">
                      <div className="relative flex-1">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                        <input
                          value={suggestSearch}
                          onChange={e => setSuggestSearch(e.target.value)}
                          placeholder="Search platforms..."
                          className="w-full bg-[#07111F] border border-[rgba(22,194,255,0.15)] rounded-lg pl-9 pr-4 py-2 text-sm text-[#E6F1FF] placeholder-[#93A4B8]/50 focus:outline-none focus:border-[rgba(22,194,255,0.5)]"
                        />
                      </div>
                      <button type="button" onClick={() => setShowSuggestBox(!showSuggestBox)}
                        className="px-3 py-2 rounded-lg text-xs bg-[rgba(22,194,255,0.08)] text-[#16C2FF] border border-[rgba(22,194,255,0.2)] hover:bg-[rgba(22,194,255,0.15)] transition-colors">
                        + Suggest New
                      </button>
                    </div>
                    {suggestSearch && (
                      <div className="bg-[#07111F] border border-[rgba(22,194,255,0.1)] rounded-lg p-2 mb-2 max-h-32 overflow-y-auto">
                        {filteredPlatforms.length > 0 ? filteredPlatforms.map(p => (
                          <div key={p.id} className="px-2 py-1.5 text-xs text-[#93A4B8] hover:text-[#E6F1FF] hover:bg-[rgba(22,194,255,0.05)] rounded cursor-pointer">
                            {p.name}
                          </div>
                        )) : (
                          <p className="text-xs text-[#93A4B8] px-2 py-1">No match found. Suggest it below!</p>
                        )}
                      </div>
                    )}
                    {showSuggestBox && (
                      <div className="flex gap-2">
                        <input
                          value={suggestPlatform}
                          onChange={e => setSuggestPlatform(e.target.value)}
                          placeholder="Platform name (e.g. Farcaster)"
                          className="flex-1 bg-[#07111F] border border-[rgba(22,194,255,0.15)] rounded-lg px-3 py-2 text-sm text-[#E6F1FF] placeholder-[#93A4B8]/50 focus:outline-none focus:border-[rgba(22,194,255,0.5)]"
                        />
                        <button type="button" onClick={handleSuggestPlatform}
                          className="px-3 py-2 rounded-lg text-xs bg-[rgba(0,229,168,0.1)] text-[#00E5A8] border border-[rgba(0,229,168,0.2)] hover:bg-[rgba(0,229,168,0.15)] transition-colors">
                          Submit
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Token Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="font-sora font-700 text-xl text-[#E6F1FF] mb-1">Token Details</h2>
                  <p className="text-sm text-[#93A4B8] font-dm">Contract information, token allocation, price, and project description.</p>
                </div>
                <div>
                  <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Token Contract Address <span className="text-[#FF4D6A]">*</span></label>
                  <input {...register('contractAddress', { required: 'Contract address is required', minLength: { value: 26, message: 'Enter a valid contract address' } })} placeholder="0x..." className={`${inputCls} font-mono`} />
                  {errors.contractAddress && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.contractAddress.message}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Total Supply</label>
                    <input {...register('totalSupply')} placeholder="e.g. 1,000,000,000 NXS" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Token Allocation <span className="text-[#FF4D6A]">*</span></label>
                    <input {...register('tokenAllocation', { required: 'Token allocation is required' })} placeholder="e.g. 10% for airdrop" className={inputCls} />
                    {errors.tokenAllocation && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.tokenAllocation.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">
                      <span className="flex items-center gap-1"><DollarSign size={11} /> Token Price</span>
                    </label>
                    <input {...register('tokenPrice')} placeholder="e.g. $0.05 or TBA" className={inputCls} />
                    {/* Live price for token symbol entered in step 1 */}
                    <LiveTokenPrice symbol={tokenSymbolValue || ''} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Short Description <span className="text-[#FF4D6A]">*</span></label>
                  <textarea {...register('shortDescription', { required: 'Description is required', minLength: { value: 40, message: 'Minimum 40 characters' }, maxLength: { value: 280, message: 'Maximum 280 characters' } })} rows={3} placeholder="Describe your project and what makes it unique..." className={`${inputCls} resize-none`} />
                  {errors.shortDescription && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.shortDescription.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Total Reward Pool <span className="text-[#FF4D6A]">*</span></label>
                  <input {...register('rewardPool', { required: 'Reward pool amount is required' })} placeholder="e.g. $500,000 or 10,000,000 NXS tokens" className={inputCls} />
                  {errors.rewardPool && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.rewardPool.message}</p>}
                </div>
                {/* Audit Report */}
                <div className="border border-[rgba(0,229,168,0.15)] rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Shield size={14} className="text-[#00E5A8]" />
                    <p className="text-xs font-sora font-600 text-[#00E5A8] uppercase tracking-wider">Audit Report</p>
                    <span className="text-[10px] text-[#93A4B8] ml-1">(Optional — admin can add later)</span>
                  </div>
                  <p className="text-xs text-[#93A4B8]">Upload your security audit report or provide a link. This builds trust with participants.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-[#93A4B8] mb-1">Upload PDF / Document</label>
                      <div className="border border-dashed border-[rgba(0,229,168,0.2)] rounded-lg p-3 text-center cursor-pointer hover:border-[rgba(0,229,168,0.4)] transition-colors"
                        onClick={() => document.getElementById('audit-upload')?.click()}>
                        <input id="audit-upload" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => setAuditFile(e.target.files?.[0] || null)} />
                        {auditFile ? (
                          <div className="flex items-center justify-center gap-2">
                            <CheckCircle size={14} className="text-[#00E5A8]" />
                            <span className="text-xs text-[#E6F1FF]">{auditFile.name}</span>
                          </div>
                        ) : (
                          <div>
                            <Upload size={16} className="text-[#93A4B8] mx-auto mb-1" />
                            <p className="text-xs text-[#93A4B8]">Click to upload</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-[#93A4B8] mb-1">Or paste audit URL</label>
                      <input
                        value={auditUrl}
                        onChange={e => setAuditUrl(e.target.value)}
                        placeholder="https://certik.com/projects/yourproject"
                        className={inputCls}
                      />
                    </div>
                  </div>
                </div>
                {/* Airdrop Phases */}
                <div className="border border-[rgba(139,92,246,0.15)] rounded-xl p-4 space-y-3">
                  <button type="button" onClick={() => setShowPhases(!showPhases)}
                    className="w-full flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Star size={14} className="text-[#A78BFA]" />
                      <p className="text-xs font-sora font-600 text-[#A78BFA] uppercase tracking-wider">Airdrop Phases</p>
                      {phases.length > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(139,92,246,0.15)] text-[#A78BFA]">{phases.length} phase{phases.length > 1 ? 's' : ''}</span>}
                    </div>
                    {showPhases ? <ChevronUp size={14} className="text-[#93A4B8]" /> : <ChevronDown size={14} className="text-[#93A4B8]" />}
                  </button>
                  {showPhases && (
                    <div className="space-y-4">
                      <p className="text-xs text-[#93A4B8]">Declare multiple phases of your airdrop (e.g. Phase 1: Whitelist, Phase 2: Public).</p>
                      {phases.map((phase, idx) => (
                        <div key={idx} className="bg-[rgba(139,92,246,0.05)] border border-[rgba(139,92,246,0.1)] rounded-lg p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-[#A78BFA]">Phase {phase.phase_number}</span>
                            <button type="button" onClick={() => removePhase(idx)} className="text-red-400 hover:text-red-300 transition-colors">
                              <Trash2 size={13} />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[10px] text-[#93A4B8] mb-1">Phase Name</label>
                              <input value={phase.phase_name} onChange={e => updatePhase(idx, 'phase_name', e.target.value)} placeholder="e.g. Whitelist Phase" className="w-full bg-[#07111F] border border-[rgba(22,194,255,0.15)] rounded px-2 py-1.5 text-xs text-[#E6F1FF] focus:outline-none" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-[#93A4B8] mb-1">Reward Amount</label>
                              <input value={phase.reward_amount} onChange={e => updatePhase(idx, 'reward_amount', e.target.value)} placeholder="e.g. 500 NXS" className="w-full bg-[#07111F] border border-[rgba(22,194,255,0.15)] rounded px-2 py-1.5 text-xs text-[#E6F1FF] focus:outline-none" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-[#93A4B8] mb-1">Start Date</label>
                              <input type="datetime-local" value={phase.start_date} onChange={e => updatePhase(idx, 'start_date', e.target.value)} className="w-full bg-[#07111F] border border-[rgba(22,194,255,0.15)] rounded px-2 py-1.5 text-xs text-[#E6F1FF] focus:outline-none" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-[#93A4B8] mb-1">End Date</label>
                              <input type="datetime-local" value={phase.end_date} onChange={e => updatePhase(idx, 'end_date', e.target.value)} className="w-full bg-[#07111F] border border-[rgba(22,194,255,0.15)] rounded px-2 py-1.5 text-xs text-[#E6F1FF] focus:outline-none" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-[10px] text-[#93A4B8] mb-1">Description</label>
                            <input value={phase.description} onChange={e => updatePhase(idx, 'description', e.target.value)} placeholder="Brief description of this phase" className="w-full bg-[#07111F] border border-[rgba(22,194,255,0.15)] rounded px-2 py-1.5 text-xs text-[#E6F1FF] focus:outline-none" />
                          </div>
                          <div>
                            <label className="block text-[10px] text-[#93A4B8] mb-1">Status</label>
                            <select value={phase.status} onChange={e => updatePhase(idx, 'status', e.target.value)} className="w-full bg-[#07111F] border border-[rgba(22,194,255,0.15)] rounded px-2 py-1.5 text-xs text-[#E6F1FF] focus:outline-none">
                              <option value="upcoming">Upcoming</option>
                              <option value="active">Active</option>
                              <option value="ended">Ended</option>
                            </select>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={addPhase}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs bg-[rgba(139,92,246,0.1)] text-[#A78BFA] border border-[rgba(139,92,246,0.2)] hover:bg-[rgba(139,92,246,0.15)] transition-colors">
                        <Plus size={12} /> Add Phase
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">
                    <span className="flex items-center gap-1.5"><Newspaper size={12} /> News / Announcements</span>
                  </label>
                  <textarea {...register('newsSection')} rows={3} placeholder="Share any recent news, announcements, or milestones about your project..." className={`${inputCls} resize-none`} />
                </div>
              </div>
            )}

            {/* Step 4: Campaign Rules */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="font-sora font-700 text-xl text-[#E6F1FF] mb-1">Campaign Rules & Timeline</h2>
                  <p className="text-sm text-[#93A4B8] font-dm">Define how users participate, campaign dates, and reward schedule.</p>
                </div>
                <div>
                  <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Airdrop Rules <span className="text-[#FF4D6A]">*</span></label>
                  <textarea {...register('airdropRules', { required: 'Airdrop rules are required', minLength: { value: 50, message: 'Minimum 50 characters' } })} rows={5} placeholder="1. Follow @YourProject on X&#10;2. Join our Telegram group&#10;3. Complete the task form&#10;4. Submit your wallet address..." className={`${inputCls} resize-none`} />
                  {errors.airdropRules && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.airdropRules.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Eligibility Requirements <span className="text-[#FF4D6A]">*</span></label>
                  <textarea {...register('eligibilityRequirements', { required: 'Eligibility requirements are required', minLength: { value: 30, message: 'Minimum 30 characters' } })} rows={3} placeholder="Open to all users worldwide except US and sanctioned regions..." className={`${inputCls} resize-none`} />
                  {errors.eligibilityRequirements && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.eligibilityRequirements.message}</p>}
                </div>
                <div className="border border-[rgba(22,194,255,0.1)] rounded-xl p-4 space-y-4">
                  <p className="text-xs font-sora font-600 text-[#16C2FF] uppercase tracking-wider">Campaign Timeline</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Start Date <span className="text-[#FF4D6A]">*</span></label>
                      <input {...register('startDate', { required: 'Start date is required' })} type="datetime-local" className={inputCls} />
                      {errors.startDate && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.startDate.message}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">End Date <span className="text-[#FF4D6A]">*</span></label>
                      <input {...register('endDate', { required: 'End date is required' })} type="datetime-local" className={inputCls} />
                      {errors.endDate && <p className="mt-1.5 text-xs text-[#FF4D6A] font-dm">{errors.endDate.message}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Distribution Date</label>
                      <input {...register('distributionDate')} type="date" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Vesting Details</label>
                      <input {...register('vestingDetails')} placeholder="e.g. 100% TGE, or 6-month linear vesting" className={inputCls} />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Additional Notes</label>
                  <textarea {...register('additionalNotes')} rows={3} placeholder="Any other information for the AIDARAM review team..." className={`${inputCls} resize-none`} />
                </div>
              </div>
            )}

            {/* Step 5: Contact & Verification */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div>
                  <h2 className="font-sora font-700 text-xl text-[#E6F1FF] mb-1">Contact & Verification</h2>
                  <p className="text-sm text-[#93A4B8] font-dm">Communication channels and final confirmation.</p>
                </div>
                <div className="border border-[rgba(22,194,255,0.1)] rounded-xl p-5 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare size={14} className="text-[#16C2FF]" />
                    <p className="text-xs font-sora font-600 text-[#16C2FF] uppercase tracking-wider">Communication Mode</p>
                  </div>
                  <p className="text-xs text-[#93A4B8] font-dm">How should we contact you for updates and verification?</p>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Telegram Handle</label>
                    <div className="relative">
                      <Send size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                      <input {...register('communicationTelegram')} placeholder="@yourhandle or t.me/yourhandle" className={iconInputCls} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">Company Email</label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                      <input {...register('communicationEmail')} type="email" placeholder="contact@yourcompany.io" className={iconInputCls} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-sora font-600 text-[#93A4B8] uppercase tracking-wider mb-1.5">WhatsApp Number</label>
                    <div className="relative">
                      <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
                      <input {...register('communicationWhatsapp')} placeholder="+1 234 567 8900" className={iconInputCls} />
                    </div>
                  </div>
                </div>
                <div className="bg-[rgba(22,194,255,0.04)] border border-[rgba(22,194,255,0.12)] rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <Info size={15} className="text-[#16C2FF] flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-sora font-600 text-sm text-[#E6F1FF] mb-1">Review Process</h4>
                      <p className="text-xs text-[#93A4B8] font-dm leading-relaxed">
                        Our team manually reviews all project submissions within 3–5 business days. We verify project legitimacy, social presence, and campaign compliance before listing.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[rgba(245,158,11,0.06)] border border-[rgba(245,158,11,0.15)] rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={15} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[#93A4B8] font-dm leading-relaxed">
                      By submitting, you confirm that all information provided is accurate and that your project's airdrop campaign will be conducted as described.
                    </p>
                  </div>
                </div>
                <div
                  className={`flex items-start gap-3 p-4 rounded-xl border transition-all cursor-pointer ${kycValue ? 'bg-[rgba(0,229,168,0.06)] border-[rgba(0,229,168,0.3)]' : 'bg-[rgba(22,194,255,0.04)] border-[rgba(22,194,255,0.15)]'}`}
                  onClick={() => { const cb = document.getElementById('kyc-checkbox') as HTMLInputElement; if (cb) cb.click(); }}
                >
                  <input id="kyc-checkbox" type="checkbox" {...register('kycVerification', { required: 'You must confirm this to submit' })} className="w-4 h-4 mt-0.5 rounded accent-[#16C2FF] cursor-pointer flex-shrink-0" />
                  <p className="text-sm font-dm text-[#E6F1FF] leading-relaxed">
                    I confirm that I am an authorized representative of this project and that all submitted information is accurate and complete.
                  </p>
                </div>
                {errors.kycVerification && <p className="text-xs text-[#FF4D6A] font-dm">{errors.kycVerification.message}</p>}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[rgba(22,194,255,0.08)]">
              <button type="button" onClick={() => setCurrentStep((s) => Math.max(s - 1, 1))} disabled={currentStep === 1}
                className="px-5 py-2.5 rounded-lg text-sm font-dm text-[#93A4B8] border border-[rgba(22,194,255,0.15)] hover:text-[#E6F1FF] hover:border-[rgba(22,194,255,0.3)] disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                Back
              </button>
              {currentStep < 5 ? (
                <button type="button" onClick={handleNext} className="btn-primary flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-sora">
                  Continue <ArrowRight size={14} />
                </button>
              ) : (
                <button type="submit" disabled={isSubmitting} className="btn-primary flex items-center gap-2 px-7 py-2.5 rounded-lg text-sm font-sora disabled:opacity-70 disabled:cursor-not-allowed min-w-[160px] justify-center">
                  {isSubmitting ? <><Loader2 size={14} className="animate-spin" /> Submitting...</> : <><CheckCircle size={14} /> Submit Project</>}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}